/* auto-gift.js v2.5 — ENVY-safe GWP with promo gating
   • Stops adding the gift when promo is OFF (and removes any auto-added gift)
   • Optional date window (storefront-timezone based if you pass __GWP_NOW_TS__)
   • Keeps v2.4 behavior: toasts + one-time reload on cart page (add/remove)
*/
(() => {
  /* ====== PROMO GATING (edit these) ====== */
  // Quick switch: set to false to turn promo OFF immediately
  // Set to true to force ON; set to null to use the optional date window or theme toggle.
  const PROMO_ACTIVE = false; // ← promo expired? keep this false

  // Optional date window (only used when PROMO_ACTIVE === null)
  // Examples: '2025-08-01T00:00:00-04:00'  through  '2025-08-20T23:59:59-04:00'
  const PROMO_START_ISO = null;
  const PROMO_END_ISO = null;

  // If you add a theme setting/toggle, you can set:
  // <script>window.__GWP_ENABLED__ = {{ settings.gwp_enable | json }}; window.__GWP_NOW_TS__ = {{ "now" | date: "%s" }};</script>
  /* ======================================= */

  /* ====== GWP CONFIG ====== */
  const THRESHOLD_CENTS = 20000;            // $200.00 => 20000 cents
  const GIFT_VARIANT_ID = 46858373693610;   // Red Cornetto Charm variant ID
  const GIFT_MARKER_KEY = '_auto_gwp';      // identifies auto-added gift
  const CART_ATTR_FLAG = '_auto_gwp_active';// cart attribute toggle
  // Toast messages
  const TOAST_ADD_MSG = 'Free gift added: Red Cornetto Charm 🎁';
  const TOAST_REMOVE_MSG = 'Free gift removed (order below $200)';
  const SHOW_REMOVE_TOAST = true;
  /* ======================== */

  let isUpdating = false;
  const onCartPage = /^\/cart(\/.*)?$/.test(location.pathname);

  // ---- promo logic ----
  function promoActiveNow() {
    if (typeof window.__GWP_ENABLED__ === 'boolean') return window.__GWP_ENABLED__;
    if (PROMO_ACTIVE !== null) return !!PROMO_ACTIVE;
    if (PROMO_START_ISO && PROMO_END_ISO) {
      const nowMs = (typeof window.__GWP_NOW_TS__ === 'number' ? window.__GWP_NOW_TS__ * 1000 : Date.now());
      const start = Date.parse(PROMO_START_ISO);
      const end = Date.parse(PROMO_END_ISO);
      if (!isNaN(start) && !isNaN(end)) return nowMs >= start && nowMs <= end;
    }
    return true; // default to ON if not explicitly configured
  }

  // ---------- toast helpers ----------
  function ensureToast() {
    let t = document.getElementById('gwp-toast');
    if (t) return t;
    if (!document.getElementById('gwp-toast-style')) {
      const style = document.createElement('style');
      style.id = 'gwp-toast-style';
      style.textContent = `
        #gwp-toast {
          position: fixed; left: 50%; transform: translateX(-50%) translateY(20px);
          bottom: 20px; z-index: 999999; padding: 12px 16px; border-radius: 999px;
          background: rgba(0,0,0,.85); color: #fff; font-size: 14px; line-height: 1.2;
          box-shadow: 0 6px 20px rgba(0,0,0,.25); opacity: 0; pointer-events: none;
          transition: opacity .2s ease, transform .2s ease;
        }
        #gwp-toast.show { opacity: 1; transform: translateX(-50%) translateY(0); }
        @media (prefers-reduced-motion: reduce) { #gwp-toast { transition: none; } }
      `;
      document.head.appendChild(style);
    }
    t = document.createElement('div');
    t.id = 'gwp-toast';
    t.setAttribute('role', 'status');
    t.setAttribute('aria-live', 'polite');
    document.body.appendChild(t);
    return t;
  }
  function showToast(msg) {
    const t = ensureToast();
    t.textContent = msg;
    t.classList.add('show');
    clearTimeout(showToast._timer);
    showToast._timer = setTimeout(() => t.classList.remove('show'), 3200);
  }

  // ---------- helpers ----------
  function isAutoGiftLine(item) {
    const v = (item.properties || {})[GIFT_MARKER_KEY];
    return v === true || v === 'true' || v === 1 || v === '1';
  }
  function softReloadOnce(key) {
    if (!onCartPage) return;
    if (sessionStorage.getItem(key) === '1') return;
    sessionStorage.setItem(key, '1');
    setTimeout(() => {
      location.reload();
      setTimeout(() => sessionStorage.removeItem(key), 1500);
    }, 150);
  }
  const softReloadAfterAdd = () => softReloadOnce('gwp_refreshed_add');
  const softReloadAfterRemove = () => softReloadOnce('gwp_refreshed_remove');

  async function getCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Failed to load cart');
    return res.json();
  }
  function findGift(cart) {
    return cart.items.find((i) => i.variant_id === GIFT_VARIANT_ID);
  }
  function subtotalExcludingGift(cart) {
    return cart.items.reduce((sum, i) => {
      const line = (i.variant_id === GIFT_VARIANT_ID) ? 0 : (i.price * i.quantity);
      return sum + line;
    }, 0);
  }
  async function addGift() {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: GIFT_VARIANT_ID, quantity: 1, properties: { [GIFT_MARKER_KEY]: 'true' } })
    }).then(r => r.json());
  }
  async function changeLine(idOrKey, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: idOrKey, quantity })
    }).then(r => r.json());
  }
  async function setCartAttr(active) {
    const attrs = active ? { [CART_ATTR_FLAG]: '1' } : { [CART_ATTR_FLAG]: '' };
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes: attrs })
      });
    } catch (_) {}
  }
  function nudgeTheme() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('ajaxCart:updated'));
  }

  // ---------- sync ----------
  async function syncGift() {
    if (isUpdating) return;
    isUpdating = true;

    try {
      const cart = await getCart();
      const qualifies = subtotalExcludingGift(cart) >= THRESHOLD_CENTS;
      const giftItem = findGift(cart);
      const cartFlagActive = (cart.attributes && cart.attributes[CART_ATTR_FLAG] === '1');
      const promoOn = promoActiveNow();

      // If promo is OFF: never add; remove any auto-added gift and exit
      if (!promoOn) {
        if (giftItem && (isAutoGiftLine(giftItem) || cartFlagActive)) {
          await changeLine(giftItem.key, 0);
          await setCartAttr(false);
          nudgeTheme();
          if (SHOW_REMOVE_TOAST) showToast(TOAST_REMOVE_MSG);
          softReloadAfterRemove();
        }
        return;
      }

      // Promo is ON: normal behavior
      if (qualifies && !giftItem) {
        await addGift().catch(() => {/* ignore 422/out-of-stock */});
        await setCartAttr(true);
        nudgeTheme();
        showToast(TOAST_ADD_MSG);
        document.dispatchEvent(new CustomEvent('gwp:added', { detail: { variantId: GIFT_VARIANT_ID } }));
        softReloadAfterAdd();
      } else if (!qualifies && giftItem && (isAutoGiftLine(giftItem) || cartFlagActive)) {
        await changeLine(giftItem.key, 0);
        await setCartAttr(false);
        nudgeTheme();
        if (SHOW_REMOVE_TOAST) showToast(TOAST_REMOVE_MSG);
        document.dispatchEvent(new CustomEvent('gwp:removed', { detail: { variantId: GIFT_VARIANT_ID } }));
        softReloadAfterRemove();
      } else if (giftItem && giftItem.quantity > 1) {
        await changeLine(giftItem.key, 1);
        nudgeTheme();
      } else if (!qualifies && !giftItem && cartFlagActive) {
        await setCartAttr(false);
      }
    } catch (e) {
      console.warn('Auto GWP error:', e);
    } finally {
      isUpdating = false;
    }
  }

  // ---------- wiring (ENVY-safe) ----------
  document.addEventListener('DOMContentLoaded', () => {
    syncGift();
    setTimeout(syncGift, 350);
  });
  document.addEventListener('ajaxCart:updated', () => setTimeout(syncGift, 120));
  document.addEventListener('cart:refresh', () => setTimeout(syncGift, 120));

  document.addEventListener('click', (e) => {
    const el = e.target.closest('a,button'); if (!el) return;
    const href = el.getAttribute('href') || '';
    if (href.includes('/cart/change')) setTimeout(syncGift, 350);
    if (el.matches('[data-qty-plus],[data-qty-minus],[data-remove],[name="remove"],[data-cart-remove],[name="updates[]"]')) {
      setTimeout(syncGift, 350);
    }
  });
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (form && form.action && form.action.includes('/cart')) setTimeout(syncGift, 500);
  });
  setInterval(syncGift, 6000);
})();

